import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles,
  RefreshCw,
  Users,
  BookOpen,
  Phone,
  Zap,
  HardDrive,
  AlertTriangle,
  AlertCircle,
  Info,
  ArrowRight,
  Clock,
  Package,
  UserCheck,
  Calendar as CalendarIcon,
  Video,
  MapPin,
  ExternalLink,
  Link2,
  ChevronLeft,
  ChevronRight,
  Briefcase,
  Plus,
  X,
  Trash2,
  Save,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AssignedTasksPanel from "../components/AssignedTasksPanel";
import GoogleTasksBoard from "../components/GoogleTasksBoard";
import AdminActivityLeaderboard from "../components/AdminActivityLeaderboard";

// Categorie eventi calendario: colore riga per parole chiave + SINONIMI
// (es. "consegna", "installazione", "montaggio" → rosso). Ordine = priorità di match.
const CAT_DEFS = [
  {
    key: "consegna",
    re: /consegn|installazion|montagg|spedizion|ritir|corrier|scaric|\bposa\b/i,
    row: "bg-rose-100 hover:bg-rose-200 text-rose-900",
    label: "Consegna",
    pillLabel: "Consegne",
    pillColor: "bg-rose-500",
    hint: "consegna, installazione, montaggio, spedizione, ritiro, corriere, scarico, posa",
  },
  {
    key: "ufficio",
    re: /uffic|riunion|meeting|videocall|\bcall\b|colloqui|telefonat|conferenz|briefing/i,
    row: "bg-emerald-100 hover:bg-emerald-200 text-emerald-900",
    label: "Ufficio",
    pillLabel: "Ufficio",
    pillColor: "bg-emerald-500",
    hint: "ufficio, riunione, meeting, call, colloquio, telefonata, conferenza, briefing",
  },
  {
    key: "sopralluogo",
    re: /sopralluog|sopraluog|cantier|rilievo|rilievi|rilevament|misurazion|\bmisure\b/i,
    row: "bg-violet-100 hover:bg-violet-200 text-violet-900",
    label: "Sopralluogo",
    pillLabel: "Sopralluogo",
    pillColor: "bg-violet-500",
    hint: "sopralluogo, cantiere, rilievo, misure, misurazione",
  },
  {
    key: "personale",
    re: /personal|ferie|permess|\bmedic|dentist|famigl|privat|compleann|palestr|vacanz/i,
    row: "bg-slate-200 hover:bg-slate-300 text-slate-900",
    label: "Personale",
    pillLabel: "Personale",
    pillColor: "bg-slate-700",
    hint: "personale, ferie, permesso, medico, dentista, famiglia, privato, compleanno, vacanza",
  },
];
const OTHER_CAT = { key: "other", row: "hover:bg-ink/5 text-ink", label: null };
const categorizeEvent = (event) => {
  const s = ((event?.summary || "") + " " + (event?.description || "")).toLowerCase();
  return CAT_DEFS.find((c) => c.re.test(s)) || OTHER_CAT;
};

const ALERT_ICONS = {
  "user-check": UserCheck,
  "phone": Phone,
  "clock": Clock,
  "package": Package,
  "zap": Zap,
  "hard-drive": HardDrive,
  "book-open": BookOpen,
};

const LEVEL_STYLES = {
  critical: {
    bg: "bg-rose-50",
    border: "border-rose-300",
    text: "text-rose-800",
    Icon: AlertCircle,
  },
  warning: {
    bg: "bg-amber-50",
    border: "border-amber-300",
    text: "text-amber-900",
    Icon: AlertTriangle,
  },
  info: {
    bg: "bg-sky-50",
    border: "border-sky-200",
    text: "text-sky-900",
    Icon: Info,
  },
};

const renderInlineMarkdown = (text, boldClass = "text-amber-300 font-semibold") => {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i} className={boldClass}>{p.slice(2, -2)}</strong>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    ),
  );
};

// Pill cliccabile per filtrare eventi calendario per categoria.
// Quando `hidden=true` mostra opacity ridotta + strikethrough (categoria nascosta).
// `count` = numero eventi di quella categoria nella settimana visualizzata.
const CategoryFilterPill = ({ k, label, color, count = 0, hidden, onToggle, hint }) => (
  <button
    type="button"
    onClick={() => onToggle(k)}
    disabled={count === 0 && !hidden}
    className={`inline-flex items-center gap-1 text-[9px] tracking-[0.15em] uppercase px-1.5 py-0.5 rounded-sm border transition ${
      hidden
        ? "text-slate-400 border-slate-200 line-through hover:text-slate-600"
        : count === 0
        ? "text-slate-300 border-transparent cursor-default"
        : "text-slate1 border-transparent hover:bg-cream2"
    }`}
    title={`${count === 0 ? `Nessun ${label.toLowerCase()} questa settimana` : hidden ? `Mostra ${label}` : `Nascondi ${label}`}${hint ? ` · Parole chiave: ${hint}` : ""}`}
    data-testid={`calendar-filter-${k}`}
    data-hidden={hidden}
    data-count={count}
    aria-pressed={!hidden}
  >
    <span className={`inline-block w-2.5 h-0.5 ${color} ${hidden || count === 0 ? "opacity-40" : ""}`}></span>
    {label}
    {count > 0 && (
      <span className={`font-mono ml-0.5 ${hidden ? "opacity-60" : "text-ink font-semibold"}`}>({count})</span>
    )}
  </button>
);


// (KpiCard rimosso: le "Cifre chiave" in Home sono state sostituite dai task personali)

// ---------- Google Calendar utilities ----------
// Palette deterministica: prima lettera dell'email → hue costante.
const ACCOUNT_COLORS = [
  { bg: "bg-rose-100", ring: "ring-rose-400", text: "text-rose-800", dot: "bg-rose-500" },
  { bg: "bg-amber-100", ring: "ring-amber-400", text: "text-amber-800", dot: "bg-amber-500" },
  { bg: "bg-emerald-100", ring: "ring-emerald-400", text: "text-emerald-800", dot: "bg-emerald-500" },
  { bg: "bg-sky-100", ring: "ring-sky-400", text: "text-sky-800", dot: "bg-sky-500" },
  { bg: "bg-violet-100", ring: "ring-violet-400", text: "text-violet-800", dot: "bg-violet-500" },
  { bg: "bg-pink-100", ring: "ring-pink-400", text: "text-pink-800", dot: "bg-pink-500" },
  { bg: "bg-teal-100", ring: "ring-teal-400", text: "text-teal-800", dot: "bg-teal-500" },
  { bg: "bg-indigo-100", ring: "ring-indigo-400", text: "text-indigo-800", dot: "bg-indigo-500" },
];
const accountStyle = (email) => {
  if (!email) return ACCOUNT_COLORS[0];
  let hash = 0;
  for (let i = 0; i < email.length; i++) hash = (hash * 31 + email.charCodeAt(i)) >>> 0;
  return ACCOUNT_COLORS[hash % ACCOUNT_COLORS.length];
};
const initialFor = (email) => (email || "?").trim().charAt(0).toUpperCase() || "?";

// Converte una data ISO (con o senza timezone) al formato ``yyyy-MM-ddTHH:mm``
// atteso da ``<input type=datetime-local>``.
const toLocalDateTimeInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
// Converte il valore dell'input ``datetime-local`` in ISO con timezone locale.
const fromLocalDateTimeInput = (val) => {
  if (!val) return "";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
};

// ---------- Event edit / create modal ----------
function EventEditModal({ event, accounts, defaultAccount, onClose, onSaved, onDeleted }) {
  const isNew = !event?.id;
  const [summary, setSummary] = useState(event?.summary || "");
  const [description, setDescription] = useState(event?.description || "");
  const [location, setLocation] = useState(event?.location || "");
  const [allDay, setAllDay] = useState(!!event?.all_day);
  const [startDt, setStartDt] = useState(toLocalDateTimeInput(event?.start_dt));
  const [endDt, setEndDt] = useState(toLocalDateTimeInput(event?.end_dt));
  const [account, setAccount] = useState(event?.owner_email || defaultAccount || accounts[0] || "");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = async () => {
    if (!summary.trim()) { toast.error("Il titolo è obbligatorio"); return; }
    if (!startDt || !endDt) { toast.error("Data inizio e fine obbligatorie"); return; }
    if (!account) { toast.error("Seleziona l'account Google"); return; }
    if (new Date(endDt) <= new Date(startDt)) {
      toast.error("La data di fine deve essere successiva a quella di inizio");
      return;
    }
    setSaving(true);
    try {
      const body = {
        account,
        summary,
        description,
        location,
        all_day: allDay,
        start_dt: fromLocalDateTimeInput(startDt),
        end_dt: fromLocalDateTimeInput(endDt),
      };
      if (isNew) {
        const r = await api.post("/admin/calendar/events", body);
        toast.success("Appuntamento creato");
        onSaved(r.data);
      } else {
        const r = await api.patch(`/admin/calendar/events/${encodeURIComponent(event.id)}`, body);
        toast.success("Appuntamento aggiornato");
        onSaved(r.data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (isNew) { onClose(); return; }
    if (!window.confirm("Eliminare definitivamente questo appuntamento?")) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/calendar/events/${encodeURIComponent(event.id)}`, {
        params: { account },
      });
      toast.success("Appuntamento eliminato");
      onDeleted(event.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Errore eliminazione");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="event-modal">
      <button
        type="button"
        aria-label="Chiudi"
        onClick={onClose}
        className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
      />
      <div className="relative bg-white rounded-sm shadow-2xl w-full max-w-lg border border-ink/10 max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-ink/10 flex items-center justify-between bg-cream/50">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 mb-0.5">
              {isNew ? "Nuovo appuntamento" : "Modifica appuntamento"}
            </div>
            <div className="font-display text-lg text-ink">
              {isNew ? "Crea evento" : (event.summary || "(senza titolo)")}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-ink/10 rounded-sm text-slate1"
            data-testid="event-modal-close"
            aria-label="Chiudi"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] tracking-[0.15em] uppercase text-slate1 block mb-1">Titolo *</label>
            <input
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Es. Riunione cliente"
              className="w-full border border-ink/20 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:border-gold outline-none"
              data-testid="event-summary-input"
              autoFocus={isNew}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] tracking-[0.15em] uppercase text-slate1 block mb-1">Inizio *</label>
              <input
                type="datetime-local"
                value={startDt}
                onChange={(e) => setStartDt(e.target.value)}
                className="w-full border border-ink/20 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:border-gold outline-none"
                data-testid="event-start-input"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.15em] uppercase text-slate1 block mb-1">Fine *</label>
              <input
                type="datetime-local"
                value={endDt}
                onChange={(e) => setEndDt(e.target.value)}
                className="w-full border border-ink/20 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:border-gold outline-none"
                data-testid="event-end-input"
              />
            </div>
          </div>

          <div>
            <label className="inline-flex items-center gap-2 text-xs text-slate1 cursor-pointer">
              <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
              Tutto il giorno
            </label>
          </div>

          <div>
            <label className="text-[10px] tracking-[0.15em] uppercase text-slate1 block mb-1">Luogo</label>
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Indirizzo o link"
              className="w-full border border-ink/20 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:border-gold outline-none"
              data-testid="event-location-input"
            />
          </div>

          <div>
            <label className="text-[10px] tracking-[0.15em] uppercase text-slate1 block mb-1">Descrizione</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Note, agenda, link..."
              className="w-full border border-ink/20 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:border-gold outline-none resize-none"
              data-testid="event-description-input"
            />
          </div>

          <div>
            <label className="text-[10px] tracking-[0.15em] uppercase text-slate1 block mb-1">Account</label>
            <select
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              disabled={!isNew}
              className="w-full border border-ink/20 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:border-gold outline-none disabled:bg-ink/5 disabled:text-slate1"
              data-testid="event-account-select"
            >
              {accounts.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            {!isNew && (
              <div className="text-[10px] text-slate1 mt-1">L&apos;account non si può cambiare (l&apos;evento esiste già in questo calendario).</div>
            )}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-ink/10 flex items-center justify-between bg-cream/30">
          {!isNew ? (
            <button
              onClick={remove}
              disabled={deleting || saving}
              className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-rose-700 hover:text-rose-900 disabled:opacity-50"
              data-testid="event-delete-btn"
            >
              <Trash2 size={12} /> {deleting ? "Elimino..." : "Elimina"}
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs tracking-[0.15em] uppercase text-slate1 hover:text-ink border border-ink/10 hover:border-ink/40 rounded-sm"
              data-testid="event-cancel-btn"
            >
              Annulla
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs tracking-[0.15em] uppercase bg-ink text-cream hover:bg-slate-800 rounded-sm disabled:opacity-50"
              data-testid="event-save-btn"
            >
              <Save size={12} /> {saving ? "Salvo..." : (isNew ? "Crea" : "Salva")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Google Calendar section (week grid Mon-Sat, multi-account) ----------
function CalendarSection() {
  const { user: authUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState([]); // lista email connesse
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [eventsError, setEventsError] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [modalEvent, setModalEvent] = useState(null); // null = closed
  const [modalOpen, setModalOpen] = useState(false);
  // Filtri categoria (persistiti localStorage). Set di key nascoste.
  const [hiddenCats, setHiddenCats] = useState(() => {
    try {
      const raw = localStorage.getItem("ah_calendar_hidden_cats");
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });
  const toggleCat = useCallback((key) => {
    setHiddenCats((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem("ah_calendar_hidden_cats", JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  }, []);

  // Calcola Lunedì della settimana con offset (0=corrente, +1=prossima, -1=scorsa)
  const getWeekBounds = useCallback((offset = 0) => {
    const now = new Date();
    const dow = now.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const daysBackToMonday = dow === 0 ? 6 : dow - 1;
    const monday = new Date(now);
    monday.setDate(now.getDate() - daysBackToMonday + offset * 7);
    monday.setHours(0, 0, 0, 0);
    const saturdayEnd = new Date(monday);
    saturdayEnd.setDate(monday.getDate() + 5);
    saturdayEnd.setHours(23, 59, 59, 999);
    return { monday, saturdayEnd };
  }, []);

  // Fetch eventi per uno specifico offset
  const fetchEventsFor = useCallback(async (offset) => {
    setEventsLoading(true);
    setEventsError(null);
    try {
      const { monday, saturdayEnd } = getWeekBounds(offset);
      const ev = await api.get("/admin/calendar/events", {
        params: {
          time_min: monday.toISOString(),
          time_max: saturdayEnd.toISOString(),
          max_results: 100,
        },
      });
      setEvents(ev.data);
      setAccounts(ev.data.accounts || []);
    } catch (err) {
      setEventsError(err?.response?.data?.detail || "Errore caricamento eventi");
    } finally {
      setEventsLoading(false);
    }
  }, [getWeekBounds]);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/calendar/status");
      setStatus(r.data);
      if (r.data.connected) {
        // Fetch account list + events in parallel
        const [accResp] = await Promise.all([
          api.get("/admin/calendar/accounts").catch(() => ({ data: { accounts: [] } })),
          fetchEventsFor(weekOffset),
        ]);
        setAccounts((accResp.data.accounts || []).map(a => a.google_email));
      }
    } catch {
      // silente
    } finally {
      setLoading(false);
    }
  }, [fetchEventsFor, weekOffset]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Ricarica eventi quando cambia weekOffset (skip primo mount, gestito da loadStatus)
  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) { isFirstRun.current = false; return; }
    if (status?.connected) fetchEventsFor(weekOffset);
  }, [weekOffset, status?.connected, fetchEventsFor]);

  const openNewEvent = () => {
    setModalEvent({});
    setModalOpen(true);
  };

  const openNewEventForDay = (date) => {
    // Pre-compila con 09:00 → 10:00 locale sul giorno cliccato
    const start = new Date(date);
    start.setHours(9, 0, 0, 0);
    const end = new Date(date);
    end.setHours(10, 0, 0, 0);
    setModalEvent({
      start_dt: start.toISOString(),
      end_dt: end.toISOString(),
      all_day: false,
    });
    setModalOpen(true);
  };

  const openEditEvent = (ev) => {
    setModalEvent(ev);
    setModalOpen(true);
  };

  const onModalSaved = () => {
    setModalOpen(false);
    setModalEvent(null);
    fetchEventsFor(weekOffset);
  };

  const onModalDeleted = () => {
    setModalOpen(false);
    setModalEvent(null);
    fetchEventsFor(weekOffset);
  };

  if (loading) {
    return <div className="h-56 bg-ink/5 rounded-sm animate-pulse" />;
  }

  if (!status?.configured) {
    return (
      <div className="bg-cream2 border border-ink/10 border-dashed rounded-sm p-5 text-center">
        <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-slate1 mb-2">
          <CalendarIcon size={11} /> Non configurato
        </div>
        <div className="text-sm text-slate1">Manca GOOGLE_CLIENT_ID/SECRET nel .env.</div>
      </div>
    );
  }

  if (!status.connected) {
    return (
      <div className="bg-white border border-ink/10 border-dashed rounded-sm p-5 flex flex-wrap items-center justify-between gap-4" data-testid="calendar-not-connected">
        <div className="inline-flex items-center gap-2 text-sm text-slate1">
          <CalendarIcon size={14} className="text-slate-400" />
          <span>Google Calendar non collegato.</span>
        </div>
        <Link
          to="/admin?tab=dropbox-docs"
          className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-ink hover:text-gold"
          data-testid="calendar-go-collegamenti"
        >
          <Link2 size={13} /> Vai a Collegamenti
        </Link>
      </div>
    );
  }

  // Categorizza evento per keyword + sinonimi (CAT_DEFS, module-level)
  const categorize = categorizeEvent;
  const categorizeKey = (event) => categorizeEvent(event).key;

  const evList = (events?.events || []).filter((e) => !hiddenCats.has(categorizeKey(e)));
  const isConsegna = (event) => categorize(event).key === "consegna";

  // 6 giorni della settimana in visualizzazione (Lun-Sab)
  const { monday } = getWeekBounds(weekOffset);
  const weekDays = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });

  // Raggruppa eventi per giorno-chiave (YYYY-MM-DD locale)
  const eventsByDay = {};
  for (const e of evList) {
    if (!e.start_dt) continue;
    const d = new Date(e.start_dt);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const key = `${y}-${m}-${day}`;
    if (!eventsByDay[key]) eventsByDay[key] = [];
    eventsByDay[key].push(e);
  }

  const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const isToday = (d) => dayKey(d) === dayKey(new Date());

  const renderEventCompact = (e) => {
    const startDt = e.start_dt ? new Date(e.start_dt) : null;
    const cat = categorize(e);
    const style = accountStyle(e.owner_email);
    const initial = initialFor(e.owner_email);
    return (
      <button
        type="button"
        key={e.id}
        onClick={() => openEditEvent(e)}
        className={`w-full text-left text-[11px] py-1 px-1.5 rounded-sm transition-colors ${cat.row}`}
        data-testid={`week-event-${e.id}`}
        title={`${e.owner_email || ""} · ${cat.label ? cat.label + " · " : ""}Clicca per modificare`}
      >
        <div className="flex items-baseline gap-1.5">
          <span
            className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-bold shrink-0 ${style.bg} ${style.text} ring-1 ${style.ring}`}
            aria-label={e.owner_email}
          >
            {initial}
          </span>
          <span className="font-mono text-[10px] opacity-80">
            {e.all_day ? "—" : startDt?.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
          </span>
          <span className="flex-1 min-w-0 truncate font-medium" title={e.summary}>
            {e.summary}
          </span>
        </div>
        {(e.location || e.hangout_link || e.attendees_count > 0) && (
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[9px] text-slate1 pl-6">
            {e.location && (
              <span className="truncate max-w-[120px]" title={e.location}>
                <MapPin size={7} className="inline" /> {e.location.slice(0, 25)}
              </span>
            )}
            {e.hangout_link && (
              <span className="text-sky-700 inline-flex items-center gap-0.5">
                <Video size={7} /> Meet
              </span>
            )}
            {e.attendees_count > 0 && (
              <span className="inline-flex items-center gap-0.5">
                <Users size={7} /> {e.attendees_count}
              </span>
            )}
          </div>
        )}
      </button>
    );
  };

  const rawEvList = events?.events || [];
  const anyConsegna = rawEvList.some(isConsegna);
  const anyCategorized = rawEvList.some((e) => categorize(e).key !== "other");
  // Conteggi per categoria (settimana visualizzata)
  const catCounts = rawEvList.reduce((acc, e) => {
    const k = categorize(e).key;
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="calendar-week-grid">
      {/* Header agenda con navigation */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 inline-flex items-center gap-1.5">
            <CalendarIcon size={11} />
            {weekOffset === 0 ? "Settimana" : weekOffset === 1 ? "Prossima settimana" : weekOffset === -1 ? "Settimana scorsa" : `+${weekOffset} settimane`}
          </div>
          {/* Nav prev/next */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setWeekOffset(o => o - 1)}
              disabled={eventsLoading}
              className="inline-flex items-center justify-center w-6 h-6 border border-ink/10 hover:border-ink/40 rounded-sm text-ink disabled:opacity-40"
              data-testid="calendar-prev-week"
              title="Settimana precedente"
            >
              <ChevronLeft size={12} />
            </button>
            {weekOffset !== 0 && (
              <button
                onClick={() => setWeekOffset(0)}
                disabled={eventsLoading}
                className="text-[9px] tracking-[0.15em] uppercase text-slate1 hover:text-ink px-1.5 py-0.5 border border-transparent hover:border-ink/20 rounded-sm disabled:opacity-40"
                data-testid="calendar-this-week"
              >
                Oggi
              </button>
            )}
            <button
              onClick={() => setWeekOffset(o => o + 1)}
              disabled={eventsLoading}
              className="inline-flex items-center justify-center w-6 h-6 border border-ink/10 hover:border-ink/40 rounded-sm text-ink disabled:opacity-40"
              data-testid="calendar-next-week"
              title="Settimana prossima"
            >
              <ChevronRight size={12} />
            </button>
          </div>
          <div className="text-[10px] text-slate1 font-mono">
            {monday.toLocaleDateString("it-IT", { day: "numeric", month: "short" })} –{" "}
            {weekDays[5].toLocaleDateString("it-IT", { day: "numeric", month: "short" })}
          </div>
          {eventsLoading && (
            <span className="text-[9px] tracking-[0.15em] uppercase text-slate1 animate-pulse">Aggiornamento...</span>
          )}
          {anyCategorized && (
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="calendar-legend">
              {CAT_DEFS.map((c) => (
                <CategoryFilterPill
                  key={c.key}
                  k={c.key}
                  label={c.pillLabel}
                  color={c.pillColor}
                  count={catCounts[c.key] || 0}
                  hidden={hiddenCats.has(c.key)}
                  onToggle={toggleCat}
                  hint={c.hint}
                />
              ))}
              {hiddenCats.size > 0 && (
                <button
                  type="button"
                  onClick={() => { setHiddenCats(new Set()); try { localStorage.removeItem("ah_calendar_hidden_cats"); } catch { /* ignore */ } }}
                  className="text-[9px] tracking-[0.15em] uppercase text-slate1 underline hover:text-ink transition"
                  data-testid="calendar-legend-reset"
                  title="Mostra tutte le categorie"
                >
                  Mostra tutto
                </button>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={openNewEvent}
            disabled={accounts.length === 0}
            className="inline-flex items-center gap-1.5 bg-ink text-cream px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase rounded-sm hover:bg-slate-800 disabled:opacity-40"
            data-testid="calendar-new-event-btn"
            title="Crea nuovo appuntamento"
          >
            <Plus size={11} /> Nuovo
          </button>
        </div>
      </div>

      {/* Account chips row (sola lettura: gestione collegamenti in "Collegamenti") */}
      <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="calendar-accounts-row">
        {accounts.map((email) => {
          const s = accountStyle(email);
          return (
            <div
              key={email}
              className={`inline-flex items-center gap-1.5 pl-1 pr-2.5 py-0.5 rounded-full border border-ink/10 bg-white ${s.text}`}
              data-testid={`calendar-account-chip-${email}`}
              title={email}
            >
              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${s.bg} ring-1 ${s.ring}`}>
                {initialFor(email)}
              </span>
              <span className="text-[11px] truncate max-w-[180px]">{email}</span>
            </div>
          );
        })}
      </div>

      {/* Grid Lun-Sab */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {weekDays.map((d) => {
          const k = dayKey(d);
          const dayEvents = eventsByDay[k] || [];
          const dayHasConsegna = dayEvents.some(isConsegna);
          const today = isToday(d);
          // Sfondo: tutti i giorni bianchi; solo oggi evidenziato in beige e i
          // giorni con consegna mantengono il bordo rosso per risaltare.
          const cellClass = today
            ? "bg-[#E8DDC7] border-gold ring-1 ring-gold/40 shadow-inner"
            : dayHasConsegna
              ? "bg-white border-rose-300"
              : "bg-white border-ink/10";
          const headerLabelClass = today
            ? "text-ink font-bold"
            : dayHasConsegna
              ? "text-rose-700 font-bold"
              : "text-ink";
          return (
            <div
              key={k}
              className={`group relative border rounded-sm p-3 min-h-[160px] ${cellClass} transition-colors`}
              data-testid={`week-day-${k}`}
            >
              <div className="flex items-baseline justify-between mb-2 pb-1 border-b border-current/10">
                <div className={`text-xs font-display uppercase ${headerLabelClass}`}>
                  <span className="tracking-wide">{d.toLocaleDateString("it-IT", { weekday: "short" })}</span>
                  <span className="ml-2 font-mono opacity-80">{d.getDate()}</span>
                  {today && <span className="ml-2 text-[9px] tracking-[0.2em] text-gold font-bold">· OGGI</span>}
                </div>
                <div className="flex items-center gap-1.5">
                  <div className={`text-[9px] tracking-[0.15em] uppercase ${today ? "text-ink/70" : "text-slate1"}`}>
                    {dayEvents.length === 0
                      ? today ? "libero" : "—"
                      : `${dayEvents.length} ev.`}
                    {dayHasConsegna && !today && <span className="text-rose-700 ml-1">· cons.</span>}
                  </div>
                  <button
                    type="button"
                    onClick={() => openNewEventForDay(d)}
                    disabled={accounts.length === 0}
                    className={`inline-flex items-center justify-center w-5 h-5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-0 ${
                      today
                        ? "bg-ink/10 hover:bg-gold hover:text-white text-ink"
                        : "bg-ink/5 hover:bg-gold hover:text-white text-ink"
                    }`}
                    data-testid={`week-day-add-${k}`}
                    title={`Nuovo evento su ${d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "short" })}`}
                    aria-label={`Aggiungi evento su ${d.toLocaleDateString("it-IT")}`}
                  >
                    <Plus size={11} strokeWidth={2.5} />
                  </button>
                </div>
              </div>
              <div className="space-y-1">
                {dayEvents.length === 0 ? (
                  <button
                    type="button"
                    onClick={() => openNewEventForDay(d)}
                    disabled={accounts.length === 0}
                    className={`w-full text-[10px] italic text-center py-3 rounded-sm transition-colors disabled:cursor-not-allowed ${
                      today
                        ? "text-ink/60 hover:bg-ink/5"
                        : "text-slate1/60 hover:text-ink hover:bg-ink/5"
                    }`}
                    data-testid={`week-day-empty-${k}`}
                    title="Clicca per aggiungere un evento in questo giorno"
                  >
                    + nuovo evento
                  </button>
                ) : (
                  dayEvents.map(renderEventCompact)
                )}
              </div>
            </div>
          );
        })}
      </div>

      {eventsError && (
        <div className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 p-2 rounded-sm">
          {eventsError}
        </div>
      )}

      {modalOpen && modalEvent && (
        <EventEditModal
          event={modalEvent}
          accounts={accounts}
          defaultAccount={(() => {
            // Default: il calendario dell'admin loggato (match su email o nome),
            // non il primo in ordine alfabetico.
            if (!accounts.length) return undefined;
            const adminEmail = (authUser?.email || "").toLowerCase();
            const adminLocal = adminEmail.split("@")[0];
            const adminFirstName = (authUser?.name || "").trim().split(/\s+/)[0]?.toLowerCase() || "";
            const match = accounts.find((a) => {
              const al = String(a).toLowerCase();
              const local = al.split("@")[0];
              return al === adminEmail
                || (adminLocal && local.includes(adminLocal))
                || (adminFirstName && local.includes(adminFirstName));
            });
            return match || accounts[0];
          })()}
          onClose={() => { setModalOpen(false); setModalEvent(null); }}
          onSaved={onModalSaved}
          onDeleted={onModalDeleted}
        />
      )}
    </div>
  );
}

// ---------- IMTask Priorities Widget ----------
const CAT_COLORS = {
  CREDITI: "bg-emerald-50 text-emerald-800 border-emerald-200",
  ACCONTI: "bg-teal-50 text-teal-800 border-teal-200",
  TELEFONARE: "bg-sky-50 text-sky-800 border-sky-200",
  INVIARE_MAIL: "bg-indigo-50 text-indigo-800 border-indigo-200",
  OFFERTE: "bg-amber-50 text-amber-800 border-amber-200",
  RECALL: "bg-orange-50 text-orange-800 border-orange-200",
  PAGARE: "bg-red-50 text-red-800 border-red-200",
  ORDINARE: "bg-yellow-50 text-yellow-800 border-yellow-200",
  ASSISTENZE: "bg-rose-50 text-rose-800 border-rose-200",
  PROGETTARE: "bg-violet-50 text-violet-800 border-violet-200",
  VERIFICARE: "bg-slate-100 text-slate-800 border-slate-300",
};

function IMTaskPriorities() {
  const [state, setState] = useState({ items: [], counts: { open: 0, overdue: 0, upcoming_7d: 0 } });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/imtask/priorities", { timeout: 15000 });
        setState(r.data);
      } catch (err) {
        // silent — widget non blocca il briefing
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="bg-ink/5 rounded-sm p-6 animate-pulse" data-testid="imtask-priorities-loading">
        <div className="h-4 w-40 bg-ink/10 rounded mb-4" />
        <div className="space-y-2">
          {[1, 2, 3].map((i) => <div key={i} className="h-14 bg-ink/5 rounded" />)}
        </div>
      </div>
    );
  }

  const isOverdue = (due) => due && due < new Date().toISOString().slice(0, 10);
  const fmtDate = (iso) => {
    if (!iso) return "—";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  };
  const fmtEuro = (n) => {
    if (n == null) return null;
    try {
      return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n));
    } catch { return `€${n}`; }
  };

  return (
    <div data-testid="imtask-priorities-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 inline-flex items-center gap-2">
          <Briefcase size={12} /> Priorità IMTask CRM
          <span className="ml-2 text-slate1/70 normal-case tracking-normal text-[10px]" data-testid="imtask-count-open">
            {state.counts.open} aperte
          </span>
          {state.counts.overdue > 0 && (
            <span className="text-rose-700 font-semibold normal-case tracking-normal text-[10px]" data-testid="imtask-count-overdue">
              · {state.counts.overdue} scadute
            </span>
          )}
          {state.counts.upcoming_7d > 0 && (
            <span className="text-amber-700 font-semibold normal-case tracking-normal text-[10px]" data-testid="imtask-count-upcoming">
              · {state.counts.upcoming_7d} entro 7gg
            </span>
          )}
        </div>
        <Link
          to="/admin/imtask"
          className="text-[10px] tracking-[0.2em] uppercase text-ink hover:text-gold inline-flex items-center gap-1"
          data-testid="imtask-priorities-open-link"
        >
          Apri CRM <ArrowRight size={11} />
        </Link>
      </div>

      {state.items.length === 0 ? (
        <div className="border border-ink/10 rounded-sm bg-cream/40 px-4 py-6 text-center text-sm text-slate1 italic" data-testid="imtask-priorities-empty">
          Nessuna attività urgente. Ottima gestione! 🎉
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
          {state.items.map((t, i) => {
            const cat = t.category || "?";
            const catStyle = CAT_COLORS[cat] || "bg-slate-50 text-slate-700 border-slate-200";
            const overdue = isOverdue(t.dueDate);
            const saldo = (t.amount != null) ? Number(t.amount || 0) - Number(t.paidAmount || 0) : null;
            return (
              <Link
                key={t.id || i}
                to="/admin/imtask"
                className="block bg-cream/40 border border-ink/10 rounded-sm p-3 hover:bg-cream hover:shadow-md transition-all"
                data-testid={`imtask-priority-${i}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[9px] tracking-[0.15em] uppercase ${catStyle}`}>
                    {cat}
                  </span>
                  <div className={`text-[10px] font-semibold ${overdue ? "text-rose-700" : "text-ink/70"} shrink-0`}>
                    {overdue && <Clock size={9} className="inline mr-0.5 -mt-0.5" />}
                    {fmtDate(t.dueDate)}
                  </div>
                </div>
                <div className="text-xs font-semibold text-ink truncate mb-0.5">{t.client || "—"}</div>
                {t.description && (
                  <div className="text-[10px] text-slate1 line-clamp-2 leading-tight">{t.description}</div>
                )}
                {saldo != null && saldo > 0 && (
                  <div className="text-[10px] text-slate1 mt-1 pt-1 border-t border-ink/5">saldo {fmtEuro(saldo)}</div>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------- Main Briefing Home ----------
export default function AdminBriefing() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // KPI/alert veloci senza AI. L'assistente AI vive nel widget flottante globale.
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fast = await api.get("/admin/briefing", { params: { skip_ai: 1 }, timeout: 20000 });
      setData(fast.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Errore caricamento briefing");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse" data-testid="briefing-loading">
        <div className="h-8 w-64 bg-ink/10 rounded" />
        <div className="h-32 bg-ink/5 rounded-sm" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="h-28 bg-ink/5 rounded-sm" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-sm p-4 text-rose-800">
        <div className="font-display text-lg mb-1">Errore</div>
        <div className="text-sm">{error}</div>
        <button onClick={load} className="mt-3 inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-rose-800 hover:text-rose-900">
          <RefreshCw size={11} /> Riprova
        </button>
      </div>
    );
  }

  if (!data) return null;

  const generatedAt = data.generated_at ? new Date(data.generated_at) : null;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[280px_1fr] gap-6" data-testid="admin-briefing-layout">
      <aside className="order-2 xl:order-1" data-testid="admin-briefing-sidebar">
        <div className="space-y-6 xl:sticky xl:top-24 xl:max-h-[calc(100vh-7rem)] xl:overflow-y-auto xl:pr-1">
          <AssignedTasksPanel currentUser={user} />
        </div>
      </aside>
      <div className="space-y-8 order-1 xl:order-2" data-testid="admin-briefing-home">
      <AdminActivityLeaderboard />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 mb-0.5">Buongiorno · {new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}</div>
          <h1 className="font-display text-2xl text-ink">Briefing di oggi</h1>
        </div>
        <div className="text-[11px] text-slate1">
          Ultimo aggiornamento: {generatedAt ? generatedAt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "—"}
        </div>
      </div>

      {/* Calendario settimanale — SOPRA il briefing (posizione richiesta) */}
      <CalendarSection />

      {/* Todo personale (Google Tasks) — a schede, subito sotto il calendario (sostituisce "Cifre chiave") */}
      <GoogleTasksBoard />

      {/* NOTE: Il card "AI Briefing" è stato rimosso in favore del widget flottante
          AdminAIChatWidget (attivo su ogni pagina). L'admin può chiedere in
          qualsiasi momento all'AI un briefing personalizzato o dati specifici. */}

      {/* Azioni prioritarie ARCHI-HUB — griglia orizzontale compatta (max 5 su desktop) */}
      {data.alerts && data.alerts.length > 0 && (
        <div data-testid="briefing-alerts">
          <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 mb-3">Azioni prioritarie</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {data.alerts.slice(0, 5).map((a, i) => {
              const style = LEVEL_STYLES[a.level] || LEVEL_STYLES.info;
              const IconAlert = ALERT_ICONS[a.icon] || style.Icon;
              const body = (
                <div
                  className={`${style.bg} border ${style.border} ${style.text} rounded-sm p-3 h-full flex flex-col justify-between hover:shadow-md transition-shadow`}
                  data-testid={`briefing-alert-${a.level}`}
                >
                  <div className="flex items-start gap-2 mb-2">
                    <IconAlert size={14} className="mt-0.5 shrink-0" />
                    <div className="font-semibold text-xs leading-snug line-clamp-2">{a.title}</div>
                  </div>
                  <div className="text-[10px] opacity-80 line-clamp-2 mb-2">{a.detail}</div>
                  {a.action_label && (
                    <div className={`text-[9px] tracking-[0.18em] uppercase inline-flex items-center gap-1 ${style.text} opacity-90`}>
                      {a.action_label} <ArrowRight size={10} />
                    </div>
                  )}
                </div>
              );
              return a.action_link ? (
                <Link key={i} to={a.action_link} className="block h-full">{body}</Link>
              ) : (
                <div key={i} className="h-full">{body}</div>
              );
            })}
          </div>
        </div>
      )}

      {/* Widget priorità IMTask CRM — compatto, griglia orizzontale (max 5) */}
      <IMTaskPriorities />
      </div>
    </div>
  );
}
